import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { Box, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

export interface AppCardProps {
	/** Display name of the app */
	displayName: string;
	/** Short description of what the app does */
	description?: string;
	/** URL to the app's icon */
	iconUrl: string;
	/** Whether the user has connected to this app */
	isConnected: boolean;
	/** Callback when the "Connect" button is clicked */
	onConnect?: () => void;
	/**
	 * Percentage of the org that uses this connector (0-100).
	 * When provided, a social proof line is rendered at the bottom of the card.
	 */
	socialProofPercentage?: number;
	testId?: string;
}

const styles = cssMap({
	card: {
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		borderWidth: token('border.width'),
		borderRadius: token('radius.large'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
	},
	cardWithSocialProof: {
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- 44px requested by designer; no matching ADS spacing token exists
		paddingBottom: '44px' as never,
		paddingLeft: token('space.150'),
		borderWidth: token('border.width'),
		borderRadius: token('radius.large'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
	},
	mainContent: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
	},
	appIconContainer: {
		display: 'flex',
		width: '40px',
		height: '40px',
		justifyContent: 'center',
		alignItems: 'center',
		flexShrink: 0,
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.xlarge'),
	},
	textAndAction: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		flex: 1,
		minWidth: '0px',
	},
	textContent: {
		flex: 1,
		minWidth: '0px',
	},
	actionContainer: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	connectedBadge: {
		display: 'flex',
		width: '80px',
		height: '28px',
		minHeight: '24px',
		paddingTop: token('space.075'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.075'),
		justifyContent: 'center',
		alignItems: 'center',
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		// Match ADS lozenge success border color (see lozenge-base.tsx iconBorderFilter).
		'--border-color': token('color.border.success'),
		// Light mode: lighten the border
		'--cm-border-color': 'white',
		'--cm-border-value': '45%',
		'--border-l-factor': '1.33',
		// Dark mode: darken the border (mirrors ADS lozenge-base.tsx dark overrides)
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-color-mode="dark"] &': {
			'--cm-border-color': 'black',
			'--border-l-factor': '0.7',
		},
		/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
		// @ts-expect-error -- color-mix value not in cssMap types
		borderColor:
			'color-mix(in oklch, var(--border-color) 100%, var(--cm-border-color) var(--cm-border-value))',
		'@supports (color: oklch(from white l c h))': {
			borderColor: 'oklch(from var(--border-color) calc(l * var(--border-l-factor)) c h)',
		},
		/* eslint-enable @atlaskit/design-system/ensure-design-token-usage */
		backgroundColor: token('color.background.success.subtler'),
		color: token('color.text.success.bolder'),
	},
	connectBadge: {
		display: 'flex',
		width: '80px',
		height: '28px',
		minHeight: '24px',
		paddingTop: token('space.075'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.075'),
		justifyContent: 'center',
		alignItems: 'center',
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	socialProofContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		position: 'absolute',
		bottom: 0,
		left: 0,
		right: 0,
		paddingTop: token('space.100'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		backgroundColor: token('elevation.surface.sunken'),
		borderBottomLeftRadius: token('radius.large'),
		borderBottomRightRadius: token('radius.large'),
		borderTopLeftRadius: 0,
		borderTopRightRadius: 0,
		overflow: 'hidden',
	},
});

export const AppCard = ({
	displayName,
	description,
	iconUrl,
	isConnected,
	onConnect,
	socialProofPercentage,
	testId,
}: AppCardProps): React.JSX.Element => {
	const intl = useIntl();

	const handleConnectClick = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			e.stopPropagation();
			onConnect?.();
		},
		[onConnect],
	);

	const hasSocialProof = socialProofPercentage !== undefined && socialProofPercentage > 0;

	return (
		<Box xcss={hasSocialProof ? styles.cardWithSocialProof : styles.card} testId={testId}>
			<Box xcss={styles.mainContent}>
				<Box xcss={styles.appIconContainer}>
					<Image src={iconUrl} alt={displayName} width={40} height={40} />
				</Box>

				<Box xcss={styles.textAndAction}>
					<Box xcss={styles.textContent}>
						<Stack>
							<Text weight="semibold" size="medium">
								{displayName}
							</Text>
							{description && (
								<Text size="small" color="color.text.subtlest">
									{description}
								</Text>
							)}
						</Stack>
					</Box>

					<Box xcss={styles.actionContainer}>
						{isConnected ? (
							<Box xcss={styles.connectedBadge}>
								<Text size="small" color="inherit">
									{intl.formatMessage(messages.connectedLabel)}
								</Text>
							</Box>
						) : (
							<Pressable xcss={styles.connectBadge} onClick={handleConnectClick}>
								<Text size="small">{intl.formatMessage(messages.connectButton)}</Text>
							</Pressable>
						)}
					</Box>
				</Box>
			</Box>

			{hasSocialProof && (
				<Box
					xcss={styles.socialProofContainer}
					testId={testId ? `${testId}-social-proof` : undefined}
				>
					<Text size="small" color="color.text.subtle">
						{intl.formatMessage(messages.socialProofOrgUsesPercent, {
							percent: Math.round(socialProofPercentage),
						})}
					</Text>
				</Box>
			)}
		</Box>
	);
};
